import React, { useEffect } from 'react'
import WhyChooseUs from '../Components/Home/WhyChooseUs'
import { useSEO } from './Seo'
import FirstSection from '../Components/Home/FirstSection'
import HeroSection from '../Components/Home/HeroSection'
import FeaturedSection from '../Components/Home/FeaturedSection'
import GameGallery from '../Components/Home/GameGallary'
import { Helmet } from 'react-helmet-async' 
import BlogList from '../Components/BlogList'

const Home = () => {

const STORAGE_KEY = "gameListPage"

useSEO({
  title: "Play Free Online Games | Best HTML5 Games at Game Zone Hub",
  description: "Play 1000+ free online games instantly. Discover action, racing, puzzle, and multiplayer games. No downloads or sign-ups required. Play now at Game Zone Hub!",
  keywords: "play free online games, free online games, html5 games, browser games, unblocked games, play games online free, Game Zone Hub",
  canonical: "https://www.gamezonehub.in/",
  ogTitle: "Play Free Online Games Instantly | Game Zone Hub",
  ogDescription: "Play the best free online games directly in your browser. Action, puzzle, racing, and more. No downloads, just play free online!",
  ogImage: "https://www.gamezonehub.in/og-image.webp",
  twitterCard: "summary_large_image",
});

// 🚀 EXPANDED SCHEMA FOR HOMEPAGE
const schemaData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "name": "Game Zone Hub",
      "url": "https://www.gamezonehub.in/",
      "description": "Play free online games instantly. The ultimate hub for HTML5 browser games without any downloads.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://www.gamezonehub.in/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are the games on Game Zone Hub completely free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, absolutely! All games on Game Zone Hub are 100% free to play. We do not require any subscriptions, hidden fees, or credit cards."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to download games to play?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No downloads are required. All our games utilize HTML5 technology, meaning you can play them instantly directly in your web browser."
          }
        },
        {
          "@type": "Question",
          "name": "Can I play on my mobile phone?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! The majority of our catalog is fully optimized for cross-platform play. Our website dynamically adjusts to your screen size for a seamless mobile experience."
          }
        },
        {
          "@type": "Question",
          "name": "Are these games safe for kids?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we curate our platform to ensure a safe, virus-free environment. We offer a wide variety of family-friendly puzzles and educational games suitable for all ages."
          }
        }
      ]
    }
  ]
};

useEffect(()=>{
  localStorage.setItem(STORAGE_KEY, String(1))
  window.scrollTo({top:"0"})
},[])

  return (
    <div className='bg-gradient-to-br from-gray-900 via-black to-gray-800 min-h-screen pt-[80px] '>
         <Helmet>
           {schemaData && (
             <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
           )}
         </Helmet>

         <FirstSection/>
         <HeroSection/>
         <FeaturedSection/>
         <WhyChooseUs/>
         <GameGallery/>
         <BlogList/>

         {/* 🚀 MASSIVELY EXTENDED HOME PAGE SEO CONTENT BLOCK FOR ADSENSE APPROVAL */}
         <section className="bg-[#0b1120] border-t border-white/5 sm:py-16 py-5  text-gray-300" data-aos="fade-up">
           <div className="container px-4 mx-auto">
             
             {/* Main SEO Heading */}
             <div className="text-center sm:mb-16 mb-10" data-aos="zoom-in">
                <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl sm:mt-0 mt-2 font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-6 ">
                  Play Free Online Games at Game Zone Hub
                </h1>
                <p className="text-gray-400 max-w-4xl mx-auto text-lg leading-relaxed">
                  Welcome to your ultimate digital playground. Game Zone Hub is dedicated to bringing you the highest quality HTML5 browser games on the internet. No lengthy downloads, no frustrating sign-ups, and absolutely no hidden fees—just pure, instant gaming entertainment available at your fingertips.
                </p>
             </div>

             {/* Detailed Overview Section */}
             <div className="grid lg:grid-cols-2 sm:gap-12 gap-6 sm:mb-16 mb-10">
               <div className="space-y-6">
                 <h2 className="text-3xl font-extrabold text-white">The Future of Browser Gaming</h2>
                 <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"></div>
                 <div className="leading-relaxed space-y-4 text-sm sm:text-base">
                   <p>
                     In the past, playing high-quality games on a computer required expensive hardware, massive hard drive space, and hours of installation time. Today, modern <strong>HTML5 and WebGL technology</strong> has completely revolutionized the industry. At Game Zone Hub, we leverage these advanced web standards to deliver stunning 3D graphics, buttery-smooth animations, and complex physics engines directly through your standard web browser.
                   </p>
                   <p>
                     Whether you are using Google Chrome, Mozilla Firefox, Apple Safari, or Microsoft Edge, our platform is optimized for peak performance. We carefully curate our collection, handpicking titles from talented independent developers and top-tier gaming studios worldwide to ensure you always have access to fresh, engaging content.
                   </p>
                 </div>
               </div>
               
               <div className="space-y-6">
                 <h2 className="text-3xl font-extrabold text-white">Cross-Platform Compatibility</h2>
                 <div className="w-20 h-1 bg-gradient-to-r from-purple-500 to-pink-400 rounded-full"></div>
                 <div className="leading-relaxed space-y-4 text-sm sm:text-base">
                   <p>
                     We believe that your gaming experience should not be limited to a desk. That is why our website and the majority of our <strong>free online games</strong> are built with responsive design in mind. 
                   </p>
                   <p>
                     You can start a challenging puzzle on your desktop PC during a break, and seamlessly continue playing similar arcade hits on your mobile smartphone or tablet while commuting. Our games automatically scale to fit your screen perfectly, supporting both traditional keyboard/mouse setups and intuitive mobile touch controls. It is gaming on your terms, whenever and wherever you want.
                   </p>
                 </div>
               </div>
             </div>

             {/* Categories Section - Crucial for LSI Keywords */}
             <div className="mb-16">
                 <h2 className="text-3xl font-extrabold text-center text-white mb-4">Explore Our Massive Game Library</h2>
                 <p className="text-center text-gray-400 mb-10 max-w-2xl mx-auto">Dive into a world of diverse genres. We categorize our games meticulously so you can find exactly what fits your mood.</p>
                 
                 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-blue-400 mb-3 flex items-center gap-2">🏎️ High-Speed Racing</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Burn digital rubber in our top-tier driving simulators. From illegal street drag racing and Formula 1 tracks to chaotic off-road monster truck rallies, satisfy your need for speed without leaving your browser.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-purple-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-purple-400 mb-3 flex items-center gap-2">🧠 Mind-Bending Puzzles</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Train your brain with our extensive collection of logic games. Enjoy classic Mahjong, Sudoku, match-3 physics puzzles, and escape room challenges designed to test your critical thinking and problem-solving skills.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-red-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-red-400 mb-3 flex items-center gap-2">⚔️ Action & Arcade</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Relive the golden era of gaming with retro-inspired arcade hits or jump into modern, fast-paced action brawlers. Battle hordes of zombies, defend your base from alien invasions, and climb the global leaderboards.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-green-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-green-400 mb-3 flex items-center gap-2">🌐 Multiplayer .IO Games</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Connect with friends and compete against players from all over the world in real-time. Our .io game category features massive battle royales and cooperative survival games that require strategy and quick reflexes.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-pink-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-pink-400 mb-3 flex items-center gap-2">🍳 Casual & Simulation</h3>
                     <p className="text-sm leading-relaxed text-gray-400">Looking to relax? Manage your own virtual farm, cook delicious meals in restaurant simulators, or dress up characters in our casual life-simulation games. Perfect for unwinding after a long day.</p>
                   </div>
                   <div className="bg-[#1e293b] p-6 rounded-2xl border border-white/5 hover:border-yellow-500/30 transition-all shadow-lg hover:-translate-y-1">
                     <h3 className="font-bold text-xl text-yellow-400 mb-3 flex items-center gap-2">🛡️ Unblocked Games</h3>
                     <p className="text-sm leading-relaxed text-gray-400">We provide secure and safe server connections so you can play your favorite unblocked games during your free time. Enjoy uninterrupted entertainment without worrying about restrictive firewalls.</p>
                   </div>
                 </div>
             </div>

             {/* SEO FAQ SECTION FOR RICH SNIPPETS */}
             <div className="bg-gradient-to-br from-[#1e293b] to-black/60 rounded-3xl sm:p-8 p-6 sm:p-12 border border-white/10 shadow-2xl mb-12" data-aos="fade-up">
                <h2 className="text-3xl font-extrabold text-center text-white mb-10">Frequently Asked Questions</h2>
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Are these games actually free?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Yes, absolutely! Every single title on Game Zone Hub is 100% free to play. We sustain our platform through non-intrusive advertisements, meaning you will never hit a paywall, need a premium subscription, or be asked for a credit card to enjoy the full game.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Do I need to download anything?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Never. All our games are built using modern web standards like HTML5 and WebGL. This means they stream directly to your web browser instantly, saving your device's storage space and keeping your operating system safe from downloadable malware.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Can I play on my mobile phone?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Yes! The vast majority of our catalog is fully optimized for cross-platform play. Our website dynamically adjusts to your screen size, providing a seamless touch-screen experience on Android smartphones, iPhones, and iPads.</p>
                    </div>
                    <div>
                        <h3 className="font-bold text-lg text-cyan-400 mb-2">Are the games safe for kids?</h3>
                        <p className="text-gray-400 leading-relaxed text-sm sm:text-base">We strictly curate our content to ensure a safe, virus-free environment. While we host games for all demographics, we have a large selection of family-friendly puzzles, math games, and educational entertainment suitable for younger players.</p>
                    </div>
                </div>
             </div>
             
             <div className="text-center bg-gradient-to-r from-blue-900/40 to-cyan-900/40 sm:p-10 p-6 rounded-3xl border border-blue-500/20 shadow-[0_0_30px_rgba(59,130,246,0.1)]">
               <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Start Playing?</h3>
               <p className="text-gray-300 mb-0 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">
                 Join thousands of daily players who have made Game Zone Hub their go-to gaming destination. Your next favorite game is just a click away. Scroll up to browse our featured categories, utilize our powerful search feature, or simply pick a title from our Editor's Choice to begin your next digital adventure today!
               </p>
             </div>
             
           </div>
         </section>

    </div>
  )
}

export default Home